import { memo } from 'react';
import { Menu, GraduationCap } from 'lucide-react';
import { useMobileMenu } from '@/shared/contexts/MobileMenuContext';
import { ActiveCycleIndicator } from './ActiveCycleIndicator';
import { useAppConfig } from '@/shared/hooks/useAppConfig';
import { useTheme } from '@/shared/providers/ThemeProvider';
import { DynamicIsland } from '@/shared/components/DynamicIsland';
import { useDynamicIslandNotifications } from '@/shared/hooks/useDynamicIslandNotifications';
import { StreakWidget } from '@/shared/components/StreakWidget';
import type { UserDTO } from '@features/auth';

interface MobileHeaderProps {
  user: UserDTO;
}

export const MobileHeader = memo(function MobileHeader({ user }: MobileHeaderProps) {
  const { toggle, isOpen } = useMobileMenu();
  const { appName, logoMedium, logoDarkMedium } = useAppConfig();
  const { resolvedTheme } = useTheme();
  const {
    unreadNotifications,
    handleNotificationClick,
    handleNotificationDismiss,
  } = useDynamicIslandNotifications();

  // Use dark logo in dark mode if available, otherwise fall back to light logo
  const currentLogo = resolvedTheme === 'dark' && logoDarkMedium ? logoDarkMedium : logoMedium;

  return (
    <>
      {/* Dynamic Island - Top Center (Mobile/Tablet Only) */}
      <DynamicIsland
        className="lg:hidden"
        notifications={unreadNotifications}
        onNotificationClick={handleNotificationClick}
        onNotificationDismiss={handleNotificationDismiss}
        position="top"
        alignment="center"
        autoExpand={true}
        autoCycle={true}
      />

      <header className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white dark:bg-gray-900 shadow-md z-40 flex items-center justify-between px-4 transition-colors">
        {/* Logo and Active Cycle */}
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-xl bg-[var(--brand-primary)] flex items-center justify-center overflow-hidden">
            {currentLogo ? (
              <img src={currentLogo} alt={appName} className="w-full h-full object-contain" />
            ) : (
              <GraduationCap className="w-5 h-5 text-white" />
            )}
          </div>
          <span className="font-semibold text-gray-900 dark:text-gray-100">{appName}</span>
          {user.role !== 'admin' && (
            <div className="hidden xs:block">
              <ActiveCycleIndicator variant="compact" />
            </div>
          )}
        </div>

        {/* Streak, User info and menu button */}
        <div className="flex items-center gap-2">
          {/* Streak Widget for students */}
          <StreakWidget size="sm" />

          <div className="text-right hidden sm:block">
            <div className="text-xs font-medium text-gray-900 dark:text-gray-100">{user.name}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400 capitalize">{user.role}</div>
          </div>
          <button
            onClick={toggle}
            className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)] focus:ring-offset-2"
            aria-label="Toggle menu"
            aria-expanded={isOpen}
            aria-controls="mobile-sidebar"
          >
            <Menu className="w-6 h-6 text-gray-700 dark:text-gray-300" />
          </button>
        </div>
      </header>
    </>
  );
});
